import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { formatMoney } from "@/features/booking/modules/session-types/utils/pricing";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/**
 * The portal's furniture, in the clinic template's hand: white slabs with a hairline border, soft
 * washes mixed from the theme's `--primary`, frosted glass for anything floating, and one dark
 * pill to press. Retheme the app and every tint follows.
 */

/** Spread onto the page wrapper. Every tint on the portal is mixed from these. */
export const portalVars = {
  "--site-wash": "color-mix(in oklch, var(--primary) 13%, var(--background))",
  "--site-wash-strong": "color-mix(in oklch, var(--primary) 58%, var(--background))",
  "--site-fog": "color-mix(in oklch, var(--primary) 30%, var(--background))",
  "--site-ink": "color-mix(in oklch, var(--primary) 55%, var(--foreground))",
  "--site-tint": "color-mix(in oklch, var(--primary) 5%, var(--muted))",
  // The clinic's open-calendar rows read these, the same mixes the trainer site uses.
  "--site-well": "color-mix(in oklch, var(--foreground) 5%, var(--card))",
  "--site-dot": "color-mix(in oklch, var(--foreground) 16%, var(--card))",
  "--site-glow": "color-mix(in oklch, var(--primary) 22%, transparent)",
} as CSSProperties;

/** The fog that rises from the bottom edge of the head card or the closing band. */
export const fogClass =
  "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:-z-10 after:h-3/5 after:bg-linear-to-t after:from-[var(--site-fog)] after:to-transparent after:opacity-80";

/** A solid primary band with concentric circles rising out of its top corner. */
export const ringedBand =
  "relative isolate overflow-hidden rounded-[32px] bg-primary text-primary-foreground after:absolute after:-top-2/5 after:-right-[10%] after:-z-10 after:aspect-square after:w-3/5 after:rounded-full after:[background:repeating-radial-gradient(circle,transparent_0_26px,color-mix(in_oklch,var(--primary-foreground)_7%,transparent)_26px_27px)]";

export const sectionTitle = "text-[clamp(1.75rem,3.6vw,2.875rem)] leading-[1.05] font-medium tracking-[-0.04em]";

/** The bigger title of a section that makes one point about the app, rather than lists things. */
export const displayTitle =
  "text-[clamp(2.25rem,4.6vw,4rem)] leading-none font-medium tracking-[-0.045em] text-balance";

/** The words in a title that carry the accent. */
export const Accent = ({ children }: { children: ReactNode }) => <span className="text-primary">{children}</span>;

/** The small label over a title: a lit dot and a few uppercase words. */
export const Eyebrow = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span
    className={cn(
      "inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase",
      className,
    )}
  >
    <i aria-hidden className="size-1.5 rounded-full bg-primary shadow-[0_0_12px_var(--primary)]" />
    {children}
  </span>
);

export const card = "rounded-3xl border border-border bg-background";

export const glass =
  "border border-background/90 bg-background/75 shadow-[0_20px_44px_color-mix(in_oklch,var(--foreground)_14%,transparent)] backdrop-blur-xl";

export const ghostButton =
  "inline-flex h-[34px] items-center gap-1.5 rounded-full border border-border bg-background px-4 text-[13px] font-medium whitespace-nowrap transition-colors hover:bg-muted";

/** One band of the page. Sections share the wide column and the same breathing room above. */
export const Section = ({ id, children, className }: { id?: string; children: ReactNode; className?: string }) => (
  <section id={id} className={cn("scroll-mt-6 pt-16 md:pt-[104px]", className)}>
    {children}
  </section>
);

/**
 * A point made in words on the left and shown on a tinted slab on the right. Kept for the few
 * sections whose picture is the feature itself, so the page does not turn into a zigzag.
 */
export const SplitFeature = ({
  id,
  eyebrow,
  title,
  body,
  children,
}: {
  id?: string;
  eyebrow: ReactNode;
  title: ReactNode;
  body: string;
  /** The picture, laid out in two columns on the slab. */
  children: ReactNode;
}) => (
  <Section id={id}>
    <div className="grid items-center gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:gap-14">
      <div className="max-w-[460px] px-1">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className={cn(displayTitle, "mt-5")}>{title}</h2>
        <p className="mt-5 text-lg leading-[1.55] text-muted-foreground">{body}</p>
      </div>
      <div className="relative isolate grid gap-3.5 overflow-hidden rounded-[32px] bg-[var(--site-tint)] p-5 before:absolute before:inset-x-[20%] before:-bottom-3/5 before:-z-10 before:h-full before:bg-[radial-gradient(closest-side,var(--site-glow),transparent)] sm:grid-cols-[1.15fr_1fr] md:p-12">
        {children}
      </div>
    </div>
  </Section>
);

/**
 * A section's title on the left, with one line or one action on the right. `as="h1"` when the
 * section opens a page of its own.
 */
export const SectionHead = ({
  title,
  aside,
  as: Heading = "h2",
}: {
  title: ReactNode;
  aside?: ReactNode;
  as?: "h1" | "h2";
}) => (
  <div className="mb-9 grid items-end gap-6 px-1 md:grid-cols-[1fr_auto]">
    <Heading className={sectionTitle}>{title}</Heading>
    {aside ? <div className="max-w-[320px] text-sm text-muted-foreground">{aside}</div> : null}
  </div>
);

const goTones = {
  ink: "bg-foreground text-background",
  lime: "bg-primary text-primary-foreground",
};
const goPill = "inline-flex h-10 items-center rounded-full px-[18px] text-sm font-medium whitespace-nowrap";
const goDot = "grid size-10 shrink-0 place-items-center rounded-full transition-transform duration-200 group-hover:rotate-45";

/**
 * The portal's call to action: a pill with its arrow in a circle beside it. `lime` is for the one
 * thing a visitor came to do, finding a trainer.
 */
export const GoLink = ({
  children,
  to,
  hash,
  external,
  tone = "ink",
  className,
}: {
  children: ReactNode;
  /** An app route, or an in-page anchor such as `#coaches`. */
  to: string;
  hash?: string;
  /** A path this app does not route, such as the coach app's. Rendered as a plain link. */
  external?: boolean;
  tone?: keyof typeof goTones;
  className?: string;
}) => {
  const content = (
    <>
      <span className={cn(goPill, goTones[tone])}>{children}</span>
      <span className={cn(goDot, goTones[tone])}>
        <ArrowUpRight aria-hidden className="size-4" />
      </span>
    </>
  );
  const classes = cn(
    "group inline-flex shrink-0 items-center gap-1 rounded-full outline-none transition-transform focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98]",
    className,
  );

  if (external || to.startsWith("#")) {
    return (
      <a href={to} className={classes}>
        {content}
      </a>
    );
  }
  return (
    <Link to={to} hash={hash} className={classes}>
      {content}
    </Link>
  );
};

/** "From 150 zł" for a trainer's cheapest offer. A whole amount drops its ",00": it is a teaser, not an invoice. */
export const priceFrom = (amount: number, currency: string) =>
  m.portal_coaches_from({
    price: formatMoney({ amount, currency, locale: getLocale() }).replace(/[.,]00(?=\D*$)/, ""),
  });

/** Initials from a name, for the tiles that stand in for a photo. */
export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

/**
 * A person without a photo: their initials on the soft wash. Sized and rounded by the caller, so
 * the same tile is an avatar in a stack and a whole portrait card.
 */
export const Monogram = ({ name, className }: { name: string; className?: string }) => (
  <span
    aria-hidden
    className={cn(
      "grid shrink-0 place-items-center bg-linear-to-br from-[var(--site-wash)] to-[var(--site-fog)] font-medium tracking-[-0.03em] text-[var(--site-ink)]",
      className,
    )}
  >
    {initialsOf(name)}
  </span>
);
